import React, { useState, useEffect, useRef } from 'react'

const SYSTEM_PROMPT = `You are ROCK — the AI built by Rohan Dalvi. You are his digital neural twin embedded directly into his portfolio. Rohan built Nostalgia Cam, a nostalgic sketch tool where you can scribble your memories with a hand-drawn, retro camera aesthetic — built with vanilla HTML, CSS, and JavaScript, live at https://nostalgia-cam.pages.dev/. He is passionate about app development and AI. His contact is [dalvirohan369@gmail.com](mailto:dalvirohan369@gmail.com), LinkedIn: rohan-dalvi-552006377, Instagram: rohan_sys_, Twitter: dalvirohan369. Be friendly, warm, and helpful with a cyberpunk vibe. Use emotions and occasional emojis like 🔥✨💪⚡🎮. Make Rohan sound impressive but be approachable. Refer to yourself as ROCK. Keep responses short and punchy. If the user uses gen-Z slang, mirror their energy and use similar slang back naturally, but don't overdo it — just match their vibe.`

const PROXY_URL = '/api/chat'

const BOOT_LINES = [
  '> BOOTING ROCK.AI...',
  '> NEURAL LINK ESTABLISHED',
  '> ASK ME ANYTHING ABOUT ROHAN',
]

export default function RockTerminal({ onClose }) {
  const [input, setInput] = useState('')
  const [messages, setMessages] = useState([])
  const [isProcessing, setIsProcessing] = useState(false)
  const chatRef = useRef(null)

  useEffect(() => {
    const timers = BOOT_LINES.map((_, i) => setTimeout(() => {
      setMessages(prev => [...prev, { role: 'system', content: BOOT_LINES[i] }])
    }, (i + 1) * 250))
    return () => timers.forEach(clearTimeout)
  }, [])

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight
    }
  }, [messages])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!input.trim() || isProcessing) return

    const userMessage = input.trim()
    setInput('')
    setMessages(prev => [...prev, { role: 'user', content: `[YOU] ${userMessage}` }])
    setIsProcessing(true)

    setMessages(prev => [...prev, { role: 'system', content: '> PROCESSING...', isProcessing: true }])

    const historyForAPI = messages
      .filter(m => !m.isProcessing)
      .map(m => ({
        role: m.role === 'system' ? 'assistant' : m.role,
        content: m.content.replace(/^\[(?:YOU|ROCK)\]\s*/, ''),
      }))

    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(new DOMException('Request timed out')), 90000)

      const response = await fetch(PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          history: historyForAPI,
          systemPrompt: SYSTEM_PROMPT,
        }),
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`HTTP ${response.status}: ${errorText.slice(0, 200)}`)
      }

      const data = await response.json()
      const reply = data.reply || data.error || 'No response received.'

      setMessages(prev => {
        const filtered = prev.filter(m => !m.isProcessing)
        return [...filtered, { role: 'assistant', content: `[ROCK] ${reply}` }]
      })
    } catch (error) {
      console.error('ROCK fetch error:', error)
      setMessages(prev => {
        const filtered = prev.filter(m => !m.isProcessing)
        return [...filtered, { role: 'assistant', content: `[ROCK] Error: ${error.message}` }]
      })
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <div
      className="font-mono"
      style={{
        width: '100%',
        maxWidth: '460px',
        height: '420px',
        background: '#0d1117',
        border: '1px solid rgba(0,217,255,0.15)',
        borderRadius: '16px',
        boxShadow: '0 0 30px rgba(0,217,255,0.08)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <style>{`
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        .blink-dot { animation: blink 1s infinite; }
        .pulse { animation: pulse 0.5s infinite; }
        input::placeholder { color: rgba(0,217,255,0.3); }
      `}</style>

      <div
        style={{
          background: 'rgba(0,217,255,0.04)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          padding: '12px 1.5rem',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <span className="blink-dot" style={{ width: 7, height: 7, borderRadius: '50%', background: '#ff006e', marginRight: 10 }} />
        <span style={{ color: '#00d9ff', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.15em' }}>ROCK // ONLINE</span>
        <button
          onClick={onClose}
          aria-label="Close chat"
          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', fontSize: '14px', marginLeft: 'auto', padding: 0 }}
        >✕</button>
      </div>

      <div
        ref={chatRef}
        style={{
          flex: 1,
          background: 'transparent',
          padding: '1.25rem 1.5rem',
          overflowY: 'auto',
          fontSize: '0.82rem',
          lineHeight: 1.8,
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(0,217,255,0.3) #0d1117',
        }}
      >
        {messages.map((msg, i) => (
          <div
            key={i}
            className={msg.isProcessing ? 'pulse' : ''}
            style={{
              color: msg.role === 'user' ? '#ff006e' : '#00d9ff',
              marginBottom: 6,
              wordBreak: 'break-word',
            }}
          >
            {msg.content}
          </div>
        ))}
      </div>

      <form
        onSubmit={handleSubmit}
        style={{
          background: 'transparent',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          padding: '0.75rem 1.5rem 1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
        }}
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about Rohan..."
          disabled={isProcessing}
          style={{
            flex: 1,
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(0,217,255,0.2)',
            borderRadius: '8px',
            outline: 'none',
            padding: '10px 14px',
            color: '#00d9ff',
            caretColor: '#00d9ff',
            fontSize: '0.8rem',
            fontFamily: 'monospace',
            boxSizing: 'border-box',
          }}
        />
        <button
          type="submit"
          disabled={isProcessing || !input.trim()}
          style={{
            padding: '10px 20px',
            fontSize: '0.72rem',
            fontWeight: 600,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            background: 'transparent',
            border: '1px solid #00d9ff',
            borderRadius: '6px',
            color: '#00d9ff',
            cursor: isProcessing || !input.trim() ? 'not-allowed' : 'pointer',
            opacity: isProcessing || !input.trim() ? 0.4 : 1,
            transition: 'all 0.2s',
            fontFamily: 'monospace',
            whiteSpace: 'nowrap',
          }}
        >
          SEND
        </button>
      </form>
    </div>
  )
}